/**
 * CapturedPieces Component for Checkers
 *
 * Displays the count of captured pieces for each player and material advantage.
 * Updates immediately when a capture occurs.
 *
 * Requirements: 43.1, 43.2, 43.3
 */

import React from 'react';
import { PlayerColor } from '../types';
import './CapturedPieces.css';

export interface CheckersCapturedPiecesProps {
  capturedPieces: {
    dark: number;
    light: number;
  };
  className?: string;
}

/**
 * CapturedPieces component displays captured piece counts and material advantage.
 *
 * Requirement 43.1: Display count of captured pieces for each player
 * Requirement 43.2: Update immediately when a capture occurs
 * Requirement 43.3: Show material advantage
 */
export const CapturedPieces: React.FC<CheckersCapturedPiecesProps> = ({
  capturedPieces,
  className = '',
}) => {
  /**
   * Calculates material advantage.
   * Requirement 43.3: Show material advantage when one player has captured more pieces
   */
  const calculateAdvantage = (): {
    player: PlayerColor | null;
    advantage: number;
  } => {
    // capturedPieces.dark = number of dark pieces captured (by light)
    // capturedPieces.light = number of light pieces captured (by dark)
    // Material advantage: the player who has captured more opponent pieces
    const darkCapturedByLight = capturedPieces.dark;
    const lightCapturedByDark = capturedPieces.light;

    if (lightCapturedByDark > darkCapturedByLight) {
      return { player: 'dark', advantage: lightCapturedByDark - darkCapturedByLight };
    } else if (darkCapturedByLight > lightCapturedByDark) {
      return { player: 'light', advantage: darkCapturedByLight - lightCapturedByDark };
    }
    return { player: null, advantage: 0 };
  };

  const materialAdvantage = calculateAdvantage();

  /**
   * Renders captured pieces display for a player.
   * Shows the count of that player's pieces that have been captured.
   */
  const renderPlayerCaptures = (player: PlayerColor): JSX.Element => {
    const capturedCount = capturedPieces[player];
    const hasAdvantage =
      materialAdvantage.player !== null &&
      materialAdvantage.player !== player &&
      materialAdvantage.advantage > 0;
    // The player who captured more has the advantage, which is the *opponent* of the player
    // whose pieces were captured more. So if dark pieces captured > light pieces captured,
    // light has the advantage. We show "+N" next to the player who has the advantage.
    const showAdvantage =
      materialAdvantage.player === player && materialAdvantage.advantage > 0;

    const label = player === 'dark' ? 'Dark' : 'Light';
    const pieceSymbol = player === 'dark' ? '⚫' : '⚪';

    return (
      <div className={`checkers-captured__player checkers-captured__player--${player}`}>
        <div className="checkers-captured__label">
          {pieceSymbol} {label} Captured: {capturedCount}
          {showAdvantage && (
            <span className="checkers-captured__advantage">
              +{materialAdvantage.advantage}
            </span>
          )}
        </div>
        <div className="checkers-captured__pieces">
          {Array.from({ length: capturedCount }).map((_, i) => (
            <span
              key={i}
              className={`checkers-captured__piece checkers-captured__piece--${player}`}
              aria-hidden="true"
            >
              {pieceSymbol}
            </span>
          ))}
          {capturedCount === 0 && (
            <span className="checkers-captured__empty">None</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`checkers-captured ${className}`} aria-label="Captured pieces">
      {renderPlayerCaptures('dark')}
      {renderPlayerCaptures('light')}
    </div>
  );
};

export default CapturedPieces;
